import React, { useEffect, useState } from "react";
import { Users } from "lucide-react";

/**
 * OnlineUsersBadge — decorative "live users" indicator. Oscillates between
 * 20.000 and 50.000 with smooth random walk to feel alive. Purely visual —
 * no real presence tracking. Renders a green pulse dot and animated number.
 */
export default function OnlineUsersBadge({ className = "" }) {
  const [count, setCount] = useState(() => 22000 + Math.floor(Math.random() * 6000));

  useEffect(() => {
    // Random walk every ~1.8s: drift ±180 with occasional bigger jumps,
    // clamped to [20000, 50000], and slow bias toward the middle range.
    const tick = () => {
      setCount((prev) => {
        const jump = Math.random() < 0.08 ? 900 : 180;
        const delta = Math.round((Math.random() - 0.5) * 2 * jump);
        const bias = prev > 42000 ? -40 : prev < 24000 ? 40 : 0;
        const next = prev + delta + bias;
        return Math.max(20000, Math.min(50000, next));
      });
    };
    const id = setInterval(tick, 1800);
    return () => clearInterval(id);
  }, []);

  const formatted = count.toLocaleString("pt-BR");

  return (
    <div
      data-testid="online-users-badge"
      className={`inline-flex items-center gap-2.5 rounded-full bg-black/25 backdrop-blur-md border border-white/25 pl-2.5 pr-4 py-1.5 text-white ${className}`}
    >
      <span className="relative flex h-2.5 w-2.5">
        <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
        <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-400" />
      </span>
      <Users className="w-3.5 h-3.5 opacity-80" />
      <span className="text-xs sm:text-sm font-semibold tabular-nums tracking-tight">
        <span data-testid="online-users-count">{formatted}</span>
        <span className="opacity-80 font-normal"> pessoas online agora</span>
      </span>
    </div>
  );
}
